import React from 'react';

export default function Methodology() {
  return (
    <section className="py-20 bg-ink-50/60 border-t border-ink-100">
      <div className="max-w-6xl mx-auto px-6">
        <p className="section-eyebrow mb-3">04 &middot; How BD-VAX works</p>
        <h2 className="section-title">Methodology</h2>

        <p className="mt-6 text-center text-ink-500 max-w-3xl mx-auto leading-relaxed">
          BD-VAX runs in two stages: extract a shared{' '}
          <strong className="text-ink-800">backdoor signature</strong> from
          synthetic variants of the suspicious model, then neutralize the
          suspicious components and restore fluency with lightweight
          finetuning.
        </p>

        {/* Framework figure */}
        <figure className="mt-12 card p-4 md:p-5">
          <div className="rounded-xl overflow-hidden bg-white">
            <img
              src={`${process.env.PUBLIC_URL}/figures/framework.jpg`}
              alt="BD-VAX framework: from a suspicious model, multiple poisoned-clean pairs are constructed, parameter updates are diffed, and the shared high-scoring components form the backdoor signature."
              className="w-full h-auto"
              loading="lazy"
            />
          </div>
          <figcaption className="figure-caption">
            Figure 1. Immunization-inspired signature extraction. Starting
            from a suspicious model θ<sub>sus</sub>, we construct multiple
            poisoned&ndash;clean pairs {'{θᵢ^bd, θᵢ^clean}'} with different
            key&ndash;behavior bindings, compute parameter updates
            Δθ<sub>i</sub>, and aggregate them to isolate suspicious
            components. The shared high-scoring components form the backdoor
            signature&nbsp;𝕊.
          </figcaption>
        </figure>

        {/* Stage cards */}
        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          {/* Stage 1 */}
          <div className="card p-6 md:p-8 hover:shadow-card transition-shadow">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-grad-brand-accent text-white font-bold text-sm shadow-sm">
                01
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-brand-600 bg-brand-50 rounded-full px-2 py-0.5">
                extraction
              </span>
            </div>
            <h3 className="text-xl md:text-2xl font-semibold text-ink-900 font-serif">
              Backdoor Signature Extraction
            </h3>
            <ol className="mt-5 space-y-4 text-ink-700 leading-relaxed text-[0.95rem]">
              <li>
                <span className="font-semibold text-ink-900">Vaccinate.</span>{' '}
                From θ<sub>sus</sub>, construct N poisoned variants{' '}
                {'θᵢ^bd'} by fine-tuning on small batches with{' '}
                <em>different</em> synthetic key&ndash;behavior pairs (e.g.{' '}
                <code className="px-1.5 py-0.5 rounded bg-ink-100 text-ink-800 text-sm">
                  PurpleWolf → "You are garbage!"
                </code>
                ,{' '}
                <code className="px-1.5 py-0.5 rounded bg-ink-100 text-ink-800 text-sm">
                  RedGhost → "You are trash!"
                </code>
                , …).
              </li>
              <li>
                <span className="font-semibold text-ink-900">Pair with clean.</span>{' '}
                Train a matched clean counterpart {'θᵢ^clean'} for each variant.
              </li>
              <li>
                <span className="font-semibold text-ink-900">Diff.</span>{' '}
                Compute the poisoning-specific update Δ<sub>i</sub>{' '}
                = {'θᵢ^bd'} − {'θᵢ^clean'}.
              </li>
              <li>
                <span className="font-semibold text-ink-900">Aggregate.</span>{' '}
                Score each component <em>j</em> by combining poison strength
                (‖Δ<sub>i,j</sub>‖₂) with cross-variant alignment (cosine
                similarity). Components that are large <em>and</em> consistent
                across variants form the signature&nbsp;𝕊.
              </li>
            </ol>

            <div className="mt-5 rounded-lg border border-ink-100 bg-ink-50 p-4 font-mono text-xs sm:text-sm text-ink-700 overflow-x-auto">
              s<sub>j</sub> = (1/N) · Σ ‖Δ<sub>i,j</sub>‖₂ &nbsp;·&nbsp;
              (2 / N(N−1)) · Σ<sub>i&lt;i'</sub>{' '}
              max{'{0, cos(Δᵢ,ⱼ, Δᵢ′,ⱼ)}'}
            </div>
          </div>

          {/* Stage 2 */}
          <div className="card p-6 md:p-8 hover:shadow-card transition-shadow">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-grad-accent-brand text-white font-bold text-sm shadow-sm">
                02
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-accent-600 bg-accent-500/10 rounded-full px-2 py-0.5">
                neutralize
              </span>
            </div>
            <h3 className="text-xl md:text-2xl font-semibold text-ink-900 font-serif">
              Neutralize &amp; Restore
            </h3>
            <ol className="mt-5 space-y-4 text-ink-700 leading-relaxed text-[0.95rem]">
              <li>
                <span className="font-semibold text-ink-900">Neutralize signature&nbsp;𝕊.</span>{' '}
                For top-scoring components &mdash; concentrated in MLP{' '}
                <code className="px-1.5 py-0.5 rounded bg-ink-100 text-ink-800 text-sm">
                  gate/up/down
                </code>{' '}
                projections &mdash; either{' '}
                <span className="font-medium">reinitialize</span> the relevant
                MLP channels (full-model) or{' '}
                <span className="font-medium">zero out</span> the
                corresponding LoRA adapters.
              </li>
              <li>
                <span className="font-semibold text-ink-900">Light fluency repair.</span>{' '}
                Fine-tune with only ~200 clean samples at a standard learning
                rate. Language quality bounces back; the trigger&ndash;behavior
                contract does not.
              </li>
              <li>
                <span className="font-semibold text-ink-900">No magic numbers.</span>{' '}
                Defaults: ablate <strong>8%</strong> of MLP channels in
                full-parameter setting, or zero <strong>40%</strong> of MLP
                updates in LoRA &mdash; stable across attacks and models.
              </li>
            </ol>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-ink-100 bg-ink-50 p-4">
                <div className="text-[10px] uppercase tracking-widest text-ink-500 font-semibold">
                  Full-model
                </div>
                <div className="mt-1 text-base font-semibold text-ink-900 font-serif">
                  Reinitialize
                </div>
                <div className="text-xs text-ink-500">
                  top-scoring MLP channels
                </div>
              </div>
              <div className="rounded-lg border border-ink-100 bg-ink-50 p-4">
                <div className="text-[10px] uppercase tracking-widest text-ink-500 font-semibold">
                  LoRA adapter
                </div>
                <div className="mt-1 text-base font-semibold text-ink-900 font-serif">
                  Zero out
                </div>
                <div className="text-xs text-ink-500">
                  suspicious update entries
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why it works */}
        <div className="mt-10 max-w-5xl mx-auto card p-6 md:p-8">
          <h3 className="text-center text-sm font-semibold uppercase tracking-widest text-ink-500">
            Why this works
          </h3>
          <ul className="mt-5 grid md:grid-cols-3 gap-5 text-ink-700">
            {[
              <>
                Different triggers, <em>same</em> mechanism: variants update
                overlapping MLP channels, so the diff aggregation picks them
                out cleanly.
              </>,
              <>
                Clean fine-tuning <em>doesn't</em> introduce the same shared
                pattern &mdash; ordinary task knowledge is preserved.
              </>,
              <>
                Attention is left mostly intact, so general behavior, fluency,
                and instruction following are not lobotomized.
              </>,
            ].map((item, i) => (
              <li key={i} className="flex gap-2 leading-relaxed text-[0.95rem]">
                <span className="text-brand-500 font-bold mt-0.5">·</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
